import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import classnames from 'classnames';
import { ChevronDown, PlusCircle } from 'react-feather';
import { GenerateIcon, ICONS, Popover, PopoverContent, PopoverTrigger } from '@pileus-cloud/anodot-frontend-common';
import Tooltip from 'shared/components/andtComponents/Tooltip';
import tooltipStyles from 'shared/components/andtComponents/Tooltip.module.scss';
import { CLOUD_TYPE_IDS, mapUserActionsToAuthorizedUsers, USER_ACTIONS } from 'users/constants/usersConstants';
import { Routes } from 'shared/constants/routes';
import TopBarSearch from 'shared/components/TopBarSearch';
import EmptyOptions from 'shared/components/menuList/EmptyOptions';
import MenuItem from 'app/containers/Layout/topNavbar/components/MenuItem';
import MenuCategory from 'app/containers/Layout/topNavbar/components/MenuCategory';
import { ReactComponent as InfoIcon } from 'shared/img/icons/info.svg';
import ReadOnlyDisplayWrapper from 'shared/components/ReadOnlyDisplayWrapper';
import { CategoryAction, HierarchicalEntityCategory } from '@anodot-cost/rbac-client';
import { getCloudTypeSimpleIcon } from 'app/store/vendorsStore/vendorStoreProvider';
import { useRootStore } from 'app/contexts/RootStoreContext';
import {
  getFilteredAccounts,
  getFilteredCustomers,
  prepareCloudHirarcheyObject,
  prepareCustomerDivisionObject,
} from './accountPickerHelper';
import toast from 'shared/components/andtComponents/Toast';
import Spinner, { SPINNER_SIZES } from 'shared/components/andtComponents/Spinner';

import styles from 'app/containers/Layout/topNavbar/TopNavBar.module.scss';

const customStyles = {
  inputStyle: {
    backgroundColor: '#E9F0FF',
    height: '45px',
    border: 'none',
    color: '#8995A0',
    fontWeight: '400',
    borderRadius: 0,
    margin: '0px',
    width: '100%',
    borderBottom: '1px solid #d0dfff',
    padding: '5px 0px 0px 30px',
  },
  iconStyle: { top: '13px', left: '5px' },
  formStyle: { width: '100%' },
};

const AccountPicker = ({
  accountsList,
  currentDisplayedAccount,
  currentDisplayedCloudTypeId = null,
  handleAccountChange,
  handleChangeCustomer = null,
  classes,
}) => {
  const { usersStore, appStore, vendorMappingsStore } = useRootStore();
  const [isOpen, setIsOpen] = useState(false);
  const [isCloudProvider, setIsCloudProvider] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const { currDispUserDivisionId, currDispUserDivisionName } = usersStore;
  const navigate = useNavigate();
  const customerDivisionsList = usersStore.getAllCustomerDivisionUserAccounts();
  const [updatingDefaultAccountInfo, setUpdatingDefaultAccountInfo] = useState({ isUpdating: false, accountId: null });

  const initialDefaultAccountId = usersStore?.usersModel?.usersNotifications?.defaultAccountId;
  const [defaultAccountId, setDefaultAccountId] = useState(initialDefaultAccountId);

  useEffect(() => {
    setDefaultAccountId(initialDefaultAccountId);
  }, [initialDefaultAccountId]);

  const toggle = () => {
    setIsOpen((prev) => !prev);
  };

  const accountItemClickedHandler = async (accKey, cloudId, _, divisionName, divisionId) => {
    handleAccountChange(accKey, cloudId);
    toggle();

    const nextRoutePath = Routes.LANDING;
    if (divisionName && handleChangeCustomer) {
      handleChangeCustomer(divisionId, divisionName, accKey);
    }
    navigate(nextRoutePath, { accountChanged: true });
  };

  window.selectAccount = accountItemClickedHandler;
  window.afterSelectAccount = () => {
    // make sure dropdown is closed
    setIsOpen(false);
  };

  useEffect(() => {
    if (accountsList.length && !customerDivisionsList?.length) {
      setIsCloudProvider(true);
    }
  }, [accountsList, customerDivisionsList?.length]);

  // RENDERS //
  const renderAccounts = (cloudAndAccountsObj, currentCloudName) => {
    if (!Object.keys(cloudAndAccountsObj).length) {
      return <EmptyOptions size="71px" />;
    }
    let arrCloudProviders = Object.keys(cloudAndAccountsObj).map((id) => parseInt(id, 10));
    if (arrCloudProviders.includes(CLOUD_TYPE_IDS.MULTI)) {
      arrCloudProviders = [CLOUD_TYPE_IDS.MULTI, ...arrCloudProviders.filter((s) => s !== CLOUD_TYPE_IDS.MULTI)];
    }

    const getPinToolTip = (accountId) => {
      const isPinned = defaultAccountId === accountId;
      const pinInfo = { name: ICONS.pin.name, text: 'Pin as my default' };
      if (isPinned) {
        pinInfo.name = ICONS.unpin.name;
        pinInfo.text = 'Unpin';
      }
      return (
        <div className={styles.accountPinTooltip}>
          <GenerateIcon iconName={pinInfo.name} className={classnames({ [styles.coloredPinIcon]: !isPinned })} />
          <div>{pinInfo.text}</div>
        </div>
      );
    };

    const updateDefaultAccountId = async (e, accountId) => {
      e.stopPropagation();
      if (!updatingDefaultAccountInfo.isUpdating) {
        try {
          setUpdatingDefaultAccountInfo({ isUpdating: true, accountId });
          await usersStore.usersModel.updateDefaultAccountId({
            accountId: defaultAccountId === accountId ? null : accountId,
            userKey: usersStore?.currentDisplayedUserKey,
            isKeyCloakManagement: appStore?.isKeyCloakManagement,
          });
        } catch (e) {
          toast.error('Failed to update default account. Error: ' + e?.description);
        } finally {
          setUpdatingDefaultAccountInfo({ isUpdating: false, accountId: null });
        }
      }
    };

    return (
      <div key="renderAccounts" className={`${styles.listWrapper} show-scroll`}>
        {arrCloudProviders.map((cloudProvId) => {
          const orderedAccountsList = cloudAndAccountsObj[cloudProvId].sort((a) => {
            if (!usersStore.isAccountSingle(a?.accountKey)) {
              return -1;
            }
            return 0;
          });
          const cloudName = vendorMappingsStore.getDescriptionById(cloudProvId);
          return (
            <div key={`container-${cloudProvId}`}>
              <MenuCategory
                initial={currentCloudName === cloudName || searchInput.length > 0}
                classes={classes}
                key={cloudProvId + searchInput}
                title={cloudName}
                id={`topbar_account_cloud_${cloudProvId}`}
              >
                <div key="orderedAccountsList">
                  {orderedAccountsList.map(({ accountKey, accountId, accountName }) => (
                    <div key={`${accountKey}${accountId}${accountName}`} className={classes.itemWrap}>
                      <MenuItem
                        isChild
                        activeName={currentDisplayedAccount}
                        currentDisplayedCloudTypeId={currentDisplayedCloudTypeId}
                        classes={{ ...classes, item: styles.accountNameMenuItem }}
                        handler={accountItemClickedHandler}
                        item={{
                          key: accountKey || accountId,
                          value: accountName,
                          cloudId: cloudProvId,
                        }}
                        id={`account_id_${accountId}`}
                      >
                        <div className={styles.accountNameMenuContent}>
                          <div>{accountName}</div>
                          <Tooltip
                            title={getPinToolTip(accountId)}
                            placement="bottom"
                            arrow={true}
                            classes={{ tooltip: tooltipStyles.whiteTooltip, arrow: tooltipStyles.whiteArrow }}
                          >
                            <span className={styles.accountPinIconWrapper}>
                              {updatingDefaultAccountInfo.isUpdating &&
                              updatingDefaultAccountInfo.accountId === accountId ? (
                                <Spinner size={SPINNER_SIZES.X_SMALL} />
                              ) : (
                                <GenerateIcon
                                  onClick={(e) => updateDefaultAccountId(e, accountId)}
                                  iconName={ICONS.pin.name}
                                  className={classnames(styles.accountPinIcon, {
                                    [styles.selectedAccount]: defaultAccountId === accountId,
                                    [styles.accountUpdating]: updatingDefaultAccountInfo.isUpdating,
                                  })}
                                />
                              )}
                            </span>
                          </Tooltip>
                        </div>
                      </MenuItem>
                    </div>
                  ))}
                </div>
              </MenuCategory>
              <div key="menuDivider" className={classes.menuDivider} />
            </div>
          );
        })}
      </div>
    );
  };

  const renderCustomerAccount = (filteredCustomerObj, currentCloudName) => {
    if (!filteredCustomerObj || !Object.keys(filteredCustomerObj).length) {
      return <EmptyOptions size="71px" />;
    }
    const customerDivisionKeys = Object.keys(filteredCustomerObj).sort();
    return (
      <div key="renderCustomerAccount" className={`${styles.listWrapper} show-scroll`}>
        {customerDivisionKeys.map((customerDivision) => {
          const keys = Object.keys(filteredCustomerObj[customerDivision]);
          return (
            <>
              <MenuCategory
                initial={currDispUserDivisionName === customerDivision || searchInput.length > 0}
                classes={classes}
                key={customerDivision + searchInput}
                title={customerDivision}
                id={`topbar_account_customer_cloud_${customerDivision}`}
              >
                {keys.map((key, idx) => {
                  const cloudName = vendorMappingsStore.getDescriptionById(Number(key));
                  return (
                    <div key={`${Number(key)}-${idx}`}>
                      <MenuCategory
                        initial={currentCloudName === cloudName || searchInput.length > 0}
                        classes={classes}
                        key={key + searchInput}
                        title={cloudName}
                        id={`account_type_${key}`}
                      >
                        <div key="filteredCustomerObj">
                          {Array.isArray(filteredCustomerObj[customerDivision][key]) &&
                            filteredCustomerObj[customerDivision][key].map(
                              ({ accountId, accountKey, divisionId, accountName, cloudTypeId }) => (
                                <div
                                  key={`${accountId}${accountKey}${divisionId}${accountName}${cloudTypeId}`}
                                  className={classes.itemWrap}
                                >
                                  <MenuItem
                                    isChild
                                    activeName={currDispUserDivisionId === divisionId ? currentDisplayedAccount : ''}
                                    classes={classes}
                                    handler={accountItemClickedHandler}
                                    item={{
                                      key: accountKey || accountId,
                                      value: accountName,
                                      cloudId: cloudTypeId,
                                      divisionId,
                                      divisionName: customerDivision,
                                    }}
                                    id={`customer_account_id_${accountId}`}
                                  />
                                </div>
                              ),
                            )}
                        </div>
                      </MenuCategory>
                    </div>
                  );
                })}
              </MenuCategory>
            </>
          );
        })}
      </div>
    );
  };

  const handleSetSearchInput = (e) => {
    const { value } = e.target;
    setSearchInput(value);
  };
  const { cloudAndAccountsObj } = prepareCloudHirarcheyObject(accountsList);
  const { customerList } = prepareCustomerDivisionObject(customerDivisionsList);
  const filteredAccountsObj = getFilteredAccounts(cloudAndAccountsObj, searchInput);
  const filteredCustomerObj = getFilteredCustomers(customerList, searchInput);

  const currentCloudName = vendorMappingsStore.getDescriptionById(currentDisplayedCloudTypeId);
  const CloudTypeIcon = getCloudTypeSimpleIcon(currentDisplayedCloudTypeId);
  return (
    <Popover open={isOpen} onOpenChange={toggle}>
      <PopoverTrigger id="topbar_current_displayed_account" key="topbar_current_displayed_account">
        {currentDisplayedAccount && (
          <Tooltip title="The cloud account you are exploring" arrow>
            <div className={classes.topPickerEnv} key="topPickerEnv">
              {!!CloudTypeIcon && (
                <span key="CloudTypeIcon">
                  <CloudTypeIcon className={classes.cloudTypeIcon} />
                </span>
              )}
              <span key="currentDisplayedAccount">{currentDisplayedAccount}</span>
              <ChevronDown key="ChevronDown" size={16} />
            </div>
          </Tooltip>
        )}
      </PopoverTrigger>
      <div className={classes.popoverWrapper} key="popoverWrapper">
        <PopoverContent className={classes.accountsContainer}>
          <div key="itemWrap" className={classes.itemWrap}>
            <TopBarSearch
              inputStyle={customStyles.inputStyle}
              iconStyle={customStyles.iconStyle}
              formStyle={customStyles.formStyle}
              currentValue={searchInput}
              handler={handleSetSearchInput}
              id="topbar_user_search_panel"
              placeholder="Search accounts"
            />
          </div>
          {!!(customerDivisionsList?.length || Object.keys(customerDivisionsList || [])?.length) && (
            <div key="menuGroupCategory" className={classes.menuGroupCategory}>
              <button
                key="CLOUD_PROVIDER"
                type="button"
                className={isCloudProvider ? classes.selected : null}
                onClick={() => setIsCloudProvider(true)}
              >
                CLOUD PROVIDER
              </button>
              <button
                key="CUSTOMER"
                type="button"
                className={!isCloudProvider ? classes.selected : null}
                onClick={() => setIsCloudProvider(false)}
              >
                <span className={classes.iconWrap}>
                  CUSTOMER
                  <Tooltip title="Browse by customer to see associated accounts" arrow className="toolTip">
                    <span className={classes.infoIcon}>
                      <InfoIcon />
                    </span>
                  </Tooltip>
                </span>
              </button>
            </div>
          )}
          {isCloudProvider
            ? renderAccounts(filteredAccountsObj, currentCloudName)
            : renderCustomerAccount(filteredCustomerObj, currentCloudName, currDispUserDivisionId)}
          <ReadOnlyDisplayWrapper
            isHide={true}
            key="ReadOnlyDisplayWrapper"
            category={HierarchicalEntityCategory.Accounts}
            action={CategoryAction.Create}
            legacyUserTypeList={mapUserActionsToAuthorizedUsers.get(USER_ACTIONS.ADD_ACCOUNT)}
          >
            <div
              key="add_account_navbar"
              className={classes.item}
              role="button"
              id="add_account_navbar"
              onClick={() => {
                toggle();
                navigate(Routes.ACCOUNTS_OVERVIEW);
              }}
            >
              <PlusCircle size={16} />
              <span style={{ marginLeft: '10px' }}>Add account</span>
            </div>
          </ReadOnlyDisplayWrapper>
        </PopoverContent>
      </div>
    </Popover>
  );
};


export default AccountPicker;
